import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getWorkOrders,
  createWorkOrder,
  updateWorkOrder,
  deleteWorkOrder,
  getParts,
  getMachines,
} from '../../services/erpApi';

const STATUS_OPTIONS = ['NOT RELEASED', 'RELEASED', 'ACTIVE', 'COMPLETED', 'CANCELLED'];
const STATUS_COLORS = {
  'NOT RELEASED': 'bg-gray-600',
  RELEASED: 'bg-blue-600',
  ACTIVE: 'bg-amber-600',
  COMPLETED: 'bg-green-600',
  CANCELLED: 'bg-red-600',
};

export default function WorkOrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workOrders, setWorkOrders] = useState([]);
  const [parts, setParts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [partFilter, setPartFilter] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingWO, setEditingWO] = useState(null);
  const [formData, setFormData] = useState({
    woNumber: '',
    quantity: '',
    dueDate: '',
    scheduledDate: '',
    partId: '',
    machineId: '',
    priority: '5',
    status: 'NOT RELEASED',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadWorkOrders();
  }, [statusFilter, partFilter]);

  const loadData = async () => {
    try {
      const [partsData, machinesData] = await Promise.all([
        getParts(),
        getMachines(),
      ]);
      setParts(partsData);
      setMachines(machinesData);
      loadWorkOrders();
    } catch (err) {
      console.error(err);
    }
  };

  const loadWorkOrders = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (partFilter) filters.partId = partFilter;
      
      const data = await getWorkOrders(filters);
      const STATUS_SORT = { ACTIVE: 0, RELEASED: 1, 'NOT RELEASED': 2, COMPLETED: 3, CANCELLED: 4 };
      data.sort((a, b) => (STATUS_SORT[a.status] ?? 5) - (STATUS_SORT[b.status] ?? 5));
      setWorkOrders(data);
      setError(null);
    } catch (err) {
      setError('Failed to load work orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        machineId: formData.machineId || null,
      };
      
      if (editingWO) {
        await updateWorkOrder(editingWO.id, data);
      } else {
        await createWorkOrder(data);
      }
      setShowForm(false);
      setEditingWO(null);
      resetForm();
      loadWorkOrders();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save work order');
    }
  };

  const handleEdit = (wo) => {
    setEditingWO(wo);
    setFormData({
      woNumber: wo.woNumber,
      quantity: String(wo.quantity),
      dueDate: wo.dueDate.split('T')[0],
      scheduledDate: wo.scheduledDate ? wo.scheduledDate.split('T')[0] : '',
      partId: wo.partId,
      machineId: wo.machineId || '',
      priority: String(wo.priority),
      status: wo.status,
      notes: wo.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this work order?')) return;
    try {
      await deleteWorkOrder(id);
      loadWorkOrders();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete work order');
    }
  };

  const handleStatusChange = async (wo, newStatus) => {
    try {
      await updateWorkOrder(wo.id, { status: newStatus });
      loadWorkOrders();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const resetForm = () => {
    // Generate next WO number
    const year = new Date().getFullYear();
    const lastWO = workOrders.find(wo => wo.woNumber.startsWith(`WO-${year}`));
    let nextNum = 1;
    if (lastWO) {
      const parts = lastWO.woNumber.split('-');
      const num = parseInt(parts[parts.length - 1]);
      if (!isNaN(num)) nextNum = num + 1;
    }
    
    setFormData({
      woNumber: `WO-${year}-${String(nextNum).padStart(4, '0')}`,
      quantity: '',
      dueDate: '',
      scheduledDate: '',
      partId: '',
      machineId: '',
      priority: '5',
      status: 'NOT RELEASED',
      notes: '',
    });
  };

  const handleAddNew = () => {
    resetForm();
    setEditingWO(null);
    setShowForm(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getDaysUntilDue = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading && workOrders.length === 0) {
    return <div className="text-gray-400">Loading work orders...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Work Orders</h2>
        <button
          onClick={handleAddNew}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded text-white"
        >
          + Create Work Order
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded p-3 text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              if (e.target.value) {
                setSearchParams({ status: e.target.value });
              } else {
                setSearchParams({});
              }
            }}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Part</label>
          <select
            value={partFilter}
            onChange={(e) => setPartFilter(e.target.value)}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded"
          >
            <option value="">All Parts</option>
            {parts.map((p) => (
              <option key={p.id} value={p.id}>{p.partNumber} - {p.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={() => {
              setStatusFilter('');
              setPartFilter('');
              setSearchParams({});
            }}
            className="px-3 py-2 text-gray-400 hover:text-white"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingWO ? 'Edit Work Order' : 'Create Work Order'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">WO Number *</label>
                  <input
                    type="text"
                    value={formData.woNumber}
                    onChange={(e) => setFormData({ ...formData, woNumber: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Part *</label>
                <select
                  value={formData.partId}
                  onChange={(e) => setFormData({ ...formData, partId: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                  required
                >
                  <option value="">Select part...</option>
                  {parts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.partNumber} - {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Quantity *</label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                    required
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Priority (1-10)</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                    min="1"
                    max="10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Due Date *</label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Scheduled Date</label>
                  <input
                    type="date"
                    value={formData.scheduledDate}
                    onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Assigned Machine</label>
                <select
                  value={formData.machineId}
                  onChange={(e) => setFormData({ ...formData, machineId: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                >
                  <option value="">Not assigned</option>
                  {machines.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} {m.machineType ? `(${m.machineType})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-purple-500 focus:outline-none"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded text-white"
                >
                  {editingWO ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingWO(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded text-white"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Work Orders Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">WO #</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Part</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Qty</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Due Date</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Priority</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Machine</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {workOrders.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  No work orders found. Create one or adjust filters.
                </td>
              </tr>
            ) : (
              workOrders.map((wo) => {
                const daysUntil = getDaysUntilDue(wo.dueDate);
                const isOverdue = daysUntil < 0 && wo.status !== 'COMPLETED' && wo.status !== 'CANCELLED';
                const isUrgent = daysUntil <= 3 && daysUntil >= 0 && wo.status !== 'COMPLETED' && wo.status !== 'CANCELLED';
                
                return (
                  <tr key={wo.id} className={`hover:bg-gray-700/50 ${isOverdue ? 'bg-red-900/20' : ''}`}>
                    <td className="px-4 py-3 font-mono">{wo.woNumber}</td>
                    <td className="px-4 py-3">
                      <select
                        value={wo.status}
                        onChange={(e) => handleStatusChange(wo, e.target.value)}
                        className={`px-2 py-1 rounded text-sm text-white ${STATUS_COLORS[wo.status]}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-amber-400 text-sm">{wo.part?.partNumber}</div>
                      <div className="text-gray-400 text-sm">{wo.part?.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      {wo.completedQty > 0 ? (
                        <span>
                          {wo.completedQty}/{wo.quantity}
                        </span>
                      ) : (
                        wo.quantity
                      )}
                    </td>
                    <td className={`px-4 py-3 ${isOverdue ? 'text-red-400' : isUrgent ? 'text-amber-400' : ''}`}>
                      {formatDate(wo.dueDate)}
                      {isOverdue && <span className="ml-1 text-xs">(overdue)</span>}
                      {isUrgent && <span className="ml-1 text-xs">({daysUntil}d)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        wo.priority <= 3 ? 'bg-red-900/50 text-red-300' :
                        wo.priority <= 6 ? 'bg-amber-900/50 text-amber-300' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        P{wo.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {wo.machine?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleEdit(wo)}
                        className="text-blue-400 hover:text-blue-300 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(wo.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
